<!--
  ErrorState.vue — shared "something's wrong" card with optional illustration.
  Used by ReportPage.vue, ProcessingPage.vue, NotFoundPage.vue and CrashFallback.vue.
-->
<template>
  <div class="w-full max-w-lg mx-auto">
    <div class="card p-6 sm:p-8 md:p-10 text-center shadow-card overflow-x-hidden max-w-full">
    <!-- Illustration — only for error codes that have one -->
    <div v-if="illustration" class="mb-2 flex justify-center">
      <!-- 403: Forbidden / No Access -->
      <svg v-if="code === 403 || code === '403'" viewBox="0 0 200 160" class="w-40 h-auto" fill="none" xmlns="http://www.w3.org/2000/svg">
        <!-- Lock body -->
        <rect x="70" y="72" width="60" height="56" rx="8" fill="#FEE2E2" stroke="#FCA5A5" stroke-width="2.5"/>
        <!-- Lock shackle -->
        <path d="M82 72V52C82 42 90 32 100 32S118 42 118 52V72" stroke="#FCA5A5" stroke-width="6" stroke-linecap="round"/>
        <!-- Keyhole -->
        <circle cx="100" cy="95" r="8" fill="#FCA5A5"/>
        <path d="M100 100V108" stroke="#FCA5A5" stroke-width="4" stroke-linecap="round"/>
        <!-- Floating prohibition circles -->
        <circle cx="58" cy="58" r="16" fill="#FEE2E2" stroke="#F87171" stroke-width="2" opacity="0.5"/>
        <line x1="46.7" y1="46.7" x2="69.3" y2="69.3" stroke="#F87171" stroke-width="2" opacity="0.5"/>
        <circle cx="148" cy="44" r="12" fill="#FEE2E2" stroke="#F87171" stroke-width="2" opacity="0.35"/>
        <line x1="139.5" y1="35.5" x2="156.5" y2="52.5" stroke="#F87171" stroke-width="2" opacity="0.35"/>
        <!-- Ground shadow -->
        <ellipse cx="100" cy="140" rx="44" ry="6" fill="#F1F5F9"/>
      </svg>
      <!-- 404: Not Found -->
      <svg v-else-if="code === 404 || code === '404'" viewBox="0 0 200 160" class="w-40 h-auto" fill="none" xmlns="http://www.w3.org/2000/svg">
        <!-- Magnifying glass -->
        <circle cx="88" cy="72" r="36" stroke="#CBD5E1" stroke-width="5"/>
        <line x1="113" y1="97" x2="150" y2="134" stroke="#CBD5E1" stroke-width="5" stroke-linecap="round"/>
        <!-- Question mark inside -->
        <text x="88" y="82" text-anchor="middle" font-family="serif" font-size="36" fill="#94A3B8">?</text>
        <!-- Ground -->
        <ellipse cx="100" cy="146" rx="50" ry="6" fill="#F1F5F9"/>
      </svg>
      <!-- 500: Server Error -->
      <svg v-else-if="code >= 500" viewBox="0 0 200 160" class="w-40 h-auto" fill="none" xmlns="http://www.w3.org/2000/svg">
        <!-- Server rack -->
        <rect x="60" y="40" width="80" height="80" rx="6" fill="#FEF3C7" stroke="#FCD34D" stroke-width="2.5"/>
        <rect x="72" y="52" width="56" height="10" rx="3" fill="#FCD34D" opacity="0.6"/>
        <rect x="72" y="68" width="56" height="10" rx="3" fill="#FCD34D" opacity="0.6"/>
        <rect x="72" y="84" width="56" height="10" rx="3" fill="#FCD34D" opacity="0.6"/>
        <rect x="72" y="100" width="56" height="10" rx="3" fill="#FCD34D" opacity="0.6"/>
        <!-- Blinking LED -->
        <circle cx="80" cy="44" r="3" fill="#F59E0B"/>
        <!-- Sparks -->
        <path d="M52 30L55 38L52 36L49 38Z" fill="#FCD34D" opacity="0.6"/>
        <path d="M148 30L145 38L148 36L151 38Z" fill="#FCD34D" opacity="0.6"/>
        <!-- Ground -->
        <ellipse cx="100" cy="148" rx="48" ry="6" fill="#F1F5F9"/>
      </svg>
      <!-- Generic error -->
      <svg v-else viewBox="0 0 200 160" class="w-40 h-auto" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="100" cy="68" r="38" fill="#F1F5F9" stroke="#CBD5E1" stroke-width="2.5"/>
        <text x="100" y="78" text-anchor="middle" font-family="serif" font-size="40" fill="#94A3B8">!</text>
        <ellipse cx="100" cy="146" rx="46" ry="6" fill="#F1F5F9"/>
      </svg>
    </div>

      <div v-if="code" class="flex items-center justify-center gap-1.5 text-2xs font-mono font-bold tracking-widest uppercase mb-1 select-none" :class="toneText">
        <Icon :name="icon" :size="12" /> Error {{ code }}
      </div>
      <!-- headingTag lets a caller that renders this ONLY as a hidden overlay
           (CrashFallback.vue, kept always-mounted via v-show) opt out of
           emitting a real <h1>. Before this, every page in the app shipped a
           second, display:none <h1>Something broke</h1> plus its "Error 500"
           copy in the served HTML -- a live SEO defect on top of the app's
           own real <h1>, and the reason a leadranks.io audit reported h1_count=2
           with h1_keywords = ["something broke", "something", "broke"]. Real
           error pages (ReportPage/ProcessingPage/NotFoundPage) keep the default
           <h1>, where it's the page's actual heading. -->
      <component :is="headingTag" class="font-display text-2xl text-ink-900 mb-2 text-balance">{{ title }}</component>
      <p class="text-ink-500 text-sm leading-relaxed max-w-sm mx-auto text-pretty">{{ message }}</p>
      <div v-if="detail" class="mt-3 font-mono text-2xs text-ink-400 bg-ink-50 rounded-lg px-3 py-2 break-all">{{ detail }}</div>

      <div class="flex flex-wrap gap-3 justify-center mt-6">
        <button v-if="primaryLabel && !primaryTo" @click="$emit('primary')" class="btn-primary">
          <Icon v-if="primaryIcon" :name="primaryIcon" :size="14" /> {{ primaryLabel }}
        </button>
        <RouterLink v-else-if="primaryLabel && primaryTo" :to="primaryTo" class="btn-primary">
          <Icon v-if="primaryIcon" :name="primaryIcon" :size="14" /> {{ primaryLabel }}
        </RouterLink>

        <RouterLink v-if="secondaryLabel" :to="secondaryTo" class="btn-outline">
          <Icon v-if="secondaryIcon" :name="secondaryIcon" :size="14" /> {{ secondaryLabel }}
        </RouterLink>
      </div>

      <slot />
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import Icon from './Icon.vue';

const props = defineProps({
  code: { type: [String, Number], default: '' },
  icon: { type: String, default: 'alert-triangle' },
  tone: { type: String, default: 'danger' }, // 'danger' | 'neutral'
  title: { type: String, required: true },
  // 'h1' (default) for genuine error pages; 'div'/'p' for a hidden overlay
  // that must not contribute a heading to the document.
  headingTag: { type: String, default: 'h1' },
  message: { type: String, default: '' },
  detail: { type: String, default: '' },
  primaryLabel: { type: String, default: '' },
  primaryIcon: { type: String, default: 'refresh' },
  primaryTo: { type: [String, Object], default: null },
  secondaryLabel: { type: String, default: '' },
  secondaryIcon: { type: String, default: 'home' },
  secondaryTo: { type: [String, Object], default: '/' },
});

defineEmits(['primary']);

const toneText = computed(() => (props.tone === 'neutral' ? 'text-brand-600' : 'text-danger-600'));

// Always show an illustration — generic one when no code passed
const illustration = computed(() => true);
</script>
